import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import cloudApi from '../services/cloudApi';

// Polling intervals
const POLL_INTERVAL_ACTIVE = 5000;   // 5s when tasks are running
const POLL_INTERVAL_IDLE = 30000;    // 30s when nothing is running
const STALE_THRESHOLD = 10000;       // 10s before forcing refresh on visibility

/**
 * useAutomationsSync - Self-contained hook for automations data sync
 *
 * Handles:
 * - Infinite scroll with accumulation model
 * - Two-tier polling (lightweight sync + surgical updates)
 * - Visibility detection (pause when tab hidden)
 * - Optimistic updates with automatic rollback
 * - All action states for UI feedback
 */
export function useAutomationsSync({ workspaceId, scheduledOnly = false, filters = {}, limit = 10 }) {
  // Stable serialization of filters for dependency arrays
  const filtersKey = JSON.stringify(filters);

  // === Core State ===
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [upcomingScheduled, setUpcomingScheduled] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);

  // === Infinite Scroll State ===
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // === Action States (for UI feedback) ===
  const [runningIds, setRunningIds] = useState(new Set());
  const [stoppingIds, setStoppingIds] = useState(new Set());
  const [resumingIds, setResumingIds] = useState(new Set());
  const [loadingScheduleIds, setLoadingScheduleIds] = useState(new Set());

  // === Refs ===
  const pollTimeoutRef = useRef(null);
  const lastFetchRef = useRef(Date.now());
  const automationsRef = useRef(automations);
  const scheduleRefreshRef = useRef(null);
  const taskFingerprintsRef = useRef(new Map());

  // Keep automationsRef in sync for callbacks
  useEffect(() => {
    automationsRef.current = automations;
  }, [automations]);

  // === Derived State ===
  // Use summary counts (covers ALL workspace tasks) rather than scanning loaded automations
  const hasActiveTask = useMemo(() => {
    if (summary) {
      return (summary.running_count > 0 || summary.queued_count > 0);
    }
    // Fallback to scanning loaded automations before first summary arrives
    return automations.some(a =>
      a.timeline?.some(t => t.status === 'running' || t.status === 'queued')
    );
  }, [summary, automations]);

  const liveStats = useMemo(() => {
    let running = 0;
    let queued = 0;
    automations.forEach(a => {
      a.timeline?.forEach(t => {
        if (t.status === 'running') running++;
        if (t.status === 'queued') queued++;
      });
    });
    return { running, queued };
  }, [automations]);

  const scheduledAutomations = useMemo(() => {
    return new Set(automations.filter(a => a.schedule?.enabled).map(a => a.id));
  }, [automations]);

  // === Fingerprint Helpers ===
  const updateFingerprints = useCallback((automationsList) => {
    for (const a of automationsList) {
      const latestRun = a.timeline?.[0];
      taskFingerprintsRef.current.set(a.id, {
        updated_at: a.updated_at || null,
        latest_run_status: latestRun?.status || null,
        latest_run_updated_at: latestRun?.updatedAt || null,
      });
    }
  }, []);

  // === Core Fetch Logic ===
  const fetchAutomations = useCallback(async (offset, options = {}) => {
    const { silent = false, append = false } = options;

    if (!silent && automationsRef.current.length === 0) {
      setLoading(true);
    }
    if (append) {
      setLoadingMore(true);
    }

    try {
      const response = await cloudApi.getAutomations({
        limit,
        offset,
        scheduledOnly,
        filters
      });

      if (append) {
        // Infinite scroll: append new items, deduplicate by id
        setAutomations(prev => {
          const existingIds = new Set(prev.map(a => a.id));
          const newItems = response.automations.filter(a => !existingIds.has(a.id));
          return [...prev, ...newItems];
        });
      } else {
        // Initial load or reset: replace all
        setAutomations(response.automations);
      }

      setSummary(response.summary);
      setUpcomingScheduled(response.upcoming_scheduled || []);
      setTotalTasks(response.pagination.total);
      setHasMore(offset + limit < response.pagination.total);

      // Update fingerprints from loaded tasks
      updateFingerprints(response.automations);

      setError(null);
      lastFetchRef.current = Date.now();
    } catch (err) {
      setError(err.message);
      // Don't clear data on error - keep stale data visible
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, scheduledOnly, filtersKey, updateFingerprints]);

  // === Infinite Scroll: Load More ===
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextOffset = automationsRef.current.length;
    fetchAutomations(nextOffset, { silent: true, append: true });
  }, [loadingMore, hasMore, fetchAutomations]);

  // === Tier 1: Lightweight Sync Poll ===
  const pollSync = useCallback(async () => {
    try {
      const syncData = await cloudApi.getDashboardSync({ scheduledOnly, filters });

      // 1. Always update summary (fresh stats every cycle)
      setSummary({
        total_tasks: syncData.summary.total_tasks,
        running_count: syncData.summary.running_count,
        queued_count: syncData.summary.queued_count,
        scheduled_count: syncData.summary.scheduled_count,
        total_runs_today: syncData.summary.total_runs_today,
        failed_runs_today: syncData.summary.failed_runs_today,
        completed_runs_today: syncData.summary.completed_runs_today,
        next_scheduled_run: syncData.summary.next_scheduled_run,
      });
      setUpcomingScheduled(syncData.upcoming_scheduled || []);

      // 2. Build server state maps
      const serverIds = syncData.tasks.map(t => t.id);
      const serverIdSet = new Set(serverIds);
      const loadedIds = new Set(automationsRef.current.map(a => a.id));

      // 3. Detect new tasks (appear before the first loaded task in server order)
      const newestLoadedIndex = serverIds.findIndex(id => loadedIds.has(id));
      const prependCandidateIds = newestLoadedIndex > 0
        ? serverIds.slice(0, newestLoadedIndex)
        : [];

      // 4. Detect changed tasks among loaded tasks
      const changedIds = [];
      const fingerprints = taskFingerprintsRef.current;

      for (const serverTask of syncData.tasks) {
        if (!loadedIds.has(serverTask.id)) continue;

        const cached = fingerprints.get(serverTask.id);
        if (!cached) continue;

        // Compare fingerprints
        const serverUpdatedAt = serverTask.updated_at ? new Date(serverTask.updated_at).getTime() : null;
        const cachedUpdatedAt = cached.updated_at ? new Date(cached.updated_at).getTime() : null;
        const serverRunUpdatedAt = serverTask.latest_run_updated_at ? new Date(serverTask.latest_run_updated_at).getTime() : null;
        const cachedRunUpdatedAt = cached.latest_run_updated_at ? new Date(cached.latest_run_updated_at).getTime() : null;

        if (
          cachedUpdatedAt !== serverUpdatedAt ||
          cached.latest_run_status !== serverTask.latest_run_status ||
          cachedRunUpdatedAt !== serverRunUpdatedAt
        ) {
          changedIds.push(serverTask.id);
        }
      }

      // 5. Detect deleted tasks (loaded locally but not in server list)
      const deletedIds = automationsRef.current
        .map(a => a.id)
        .filter(id => !serverIdSet.has(id));

      if (deletedIds.length > 0) {
        setAutomations(prev => prev.filter(a => !deletedIds.includes(a.id)));
      }

      // 6. Surgical fetch for changed + new tasks
      const idsToFetch = [...new Set([...changedIds, ...prependCandidateIds])];

      if (idsToFetch.length > 0) {
        const updated = await cloudApi.getAutomationsByIds(idsToFetch);

        setAutomations(prev => {
          // Merge changed tasks (replace in place)
          let result = prev.map(a => {
            const updatedVersion = updated.automations.find(u => u.id === a.id);
            return updatedVersion || a;
          });

          // Prepend genuinely new tasks
          const existingIds = new Set(result.map(a => a.id));
          const newTasks = updated.automations.filter(a => !existingIds.has(a.id));
          if (newTasks.length > 0) {
            result = [...newTasks, ...result];
          }

          return result;
        });

        // Update fingerprints for all fetched tasks
        updateFingerprints(updated.automations);
      }

      // 7. Update totals and hasMore
      setTotalTasks(syncData.summary.total_tasks);
      setHasMore(automationsRef.current.length < syncData.summary.total_tasks);

      lastFetchRef.current = Date.now();
    } catch (err) {
      // Sync failures are non-fatal — just skip this cycle
      console.warn('Sync poll failed:', err.message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledOnly, filtersKey, updateFingerprints]);

  // === Polling Logic ===
  const scheduleRefresh = useCallback((delay = null) => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }

    const effectiveDelay = delay ?? (hasActiveTask ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_IDLE);

    pollTimeoutRef.current = setTimeout(async () => {
      try {
        await pollSync();
      } finally {
        // Chain next poll using ref to get latest scheduleRefresh
        if (!document.hidden) {
          scheduleRefreshRef.current?.();
        }
      }
    }, effectiveDelay);
  }, [hasActiveTask, pollSync]);

  // Keep scheduleRefreshRef in sync to avoid stale closures
  useEffect(() => {
    scheduleRefreshRef.current = scheduleRefresh;
  }, [scheduleRefresh]);

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      } else {
        const timeSinceLastRefresh = Date.now() - lastFetchRef.current;
        if (timeSinceLastRefresh > STALE_THRESHOLD) {
          pollSync();
        }
        scheduleRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [pollSync, scheduleRefresh]);

  // Start polling after initial load
  useEffect(() => {
    if (!loading && automations.length > 0) {
      scheduleRefresh();
    }

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [loading, scheduleRefresh, automations.length]);

  // Initial fetch & refetch on dependencies change
  useEffect(() => {
    taskFingerprintsRef.current.clear();
    // Show filtering indicator when data already loaded (not initial load)
    if (automationsRef.current.length > 0) {
      setFiltering(true);
    }
    fetchAutomations(0).finally(() => setFiltering(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, scheduledOnly, filtersKey]);

  // === Actions ===
  const runAutomation = useCallback(async (automationId) => {
    // Optimistic: Add to running set
    setRunningIds(prev => new Set([...prev, automationId]));

    // Optimistic: Insert queued timeline entry
    setAutomations(prev => prev.map(a => {
      if (a.id !== automationId) return a;
      return {
        ...a,
        timeline: [
          {
            timestamp: Date.now(),
            status: 'queued',
            runId: null,
            message: 'Task queued for execution'
          },
          ...a.timeline
        ]
      };
    }));

    try {
      const result = await cloudApi.runAutomation(automationId);

      // Clear running state
      setRunningIds(prev => {
        const next = new Set(prev);
        next.delete(automationId);
        return next;
      });

      // Update with actual runId
      if (result.taskRunId) {
        setAutomations(prev => prev.map(a => {
          if (a.id !== automationId) return a;
          const timeline = [...a.timeline];
          if (timeline[0]?.status === 'queued' && !timeline[0].runId) {
            timeline[0] = { ...timeline[0], runId: result.taskRunId };
          }
          return { ...a, timeline };
        }));
      }

      // Schedule refresh to sync
      scheduleRefresh(3000);
    } catch (err) {
      // Rollback
      setRunningIds(prev => {
        const next = new Set(prev);
        next.delete(automationId);
        return next;
      });

      setAutomations(prev => prev.map(a => {
        if (a.id !== automationId) return a;
        return {
          ...a,
          timeline: a.timeline.filter(t => t.runId !== null || t.status !== 'queued')
        };
      }));

      throw err;
    }
  }, [scheduleRefresh]);

  const stopAutomation = useCallback(async (automation) => {
    const runningRun = automation.timeline.find(
      t => t.status === 'running' || t.status === 'queued'
    );

    if (!runningRun?.runId) {
      throw new Error('No running task found');
    }

    const originalStatus = runningRun.status;
    const originalMessage = runningRun.message;

    // Optimistic: Add to stopping set
    setStoppingIds(prev => new Set([...prev, automation.id]));

    // Optimistic: Update timeline status
    setAutomations(prev => prev.map(a => {
      if (a.id !== automation.id) return a;
      return {
        ...a,
        timeline: a.timeline.map(t =>
          t.runId === runningRun.runId
            ? { ...t, status: 'stopped', message: 'Stopping...' }
            : t
        )
      };
    }));

    try {
      await cloudApi.stopAutomation(runningRun.runId);

      // Clear stopping state
      setStoppingIds(prev => {
        const next = new Set(prev);
        next.delete(automation.id);
        return next;
      });

      // Schedule refresh to sync
      scheduleRefresh(1000);
    } catch (err) {
      // Rollback
      setStoppingIds(prev => {
        const next = new Set(prev);
        next.delete(automation.id);
        return next;
      });

      setAutomations(prev => prev.map(a => {
        if (a.id !== automation.id) return a;
        return {
          ...a,
          timeline: a.timeline.map(t =>
            t.runId === runningRun.runId
              ? { ...t, status: originalStatus, message: originalMessage }
              : t
          )
        };
      }));

      throw err;
    }
  }, [scheduleRefresh]);

  const resumeAutomation = useCallback(async (automation, failedRunId) => {
    // Optimistic: track resuming state
    setResumingIds(prev => new Set([...prev, automation.id]));

    // Optimistic: prepend a queued entry to show the new run starting
    setAutomations(prev => prev.map(a => {
      if (a.id !== automation.id) return a;
      return {
        ...a,
        timeline: [
          { timestamp: Date.now(), status: 'queued', runId: null, message: 'Resuming from checkpoint...' },
          ...a.timeline
        ]
      };
    }));

    try {
      const result = await cloudApi.resumeTaskRun(failedRunId);

      setResumingIds(prev => {
        const next = new Set(prev);
        next.delete(automation.id);
        return next;
      });

      // Update the optimistic queued entry with the real runId
      if (result.newRunId) {
        setAutomations(prev => prev.map(a => {
          if (a.id !== automation.id) return a;
          const timeline = [...a.timeline];
          if (timeline[0]?.status === 'queued' && !timeline[0].runId) {
            timeline[0] = { ...timeline[0], runId: result.newRunId };
          }
          return { ...a, timeline };
        }));
      }

      scheduleRefresh(3000);
    } catch (err) {
      // Rollback
      setResumingIds(prev => {
        const next = new Set(prev);
        next.delete(automation.id);
        return next;
      });

      setAutomations(prev => prev.map(a => {
        if (a.id !== automation.id) return a;
        return {
          ...a,
          timeline: a.timeline.filter(t => t.runId !== null || t.status !== 'queued')
        };
      }));

      throw err;
    }
  }, [scheduleRefresh]);

  const updateSchedule = useCallback(async (automationId, schedule) => {
    // Add loading state
    setLoadingScheduleIds(prev => new Set([...prev, automationId]));

    // Optimistic update
    setAutomations(prev => prev.map(a =>
      a.id === automationId ? { ...a, schedule } : a
    ));

    try {
      await cloudApi.updateAutomationSchedule(automationId, schedule);
      // Schedule refresh to get server-computed nextRun
      scheduleRefresh(500);
    } catch (err) {
      // Rollback via refresh
      fetchAutomations(0, { silent: true });
      throw err;
    } finally {
      setLoadingScheduleIds(prev => {
        const next = new Set(prev);
        next.delete(automationId);
        return next;
      });
    }
  }, [scheduleRefresh, fetchAutomations]);

  const updateAutomationSettings = useCallback(async (automationId, updates) => {
    try {
      await cloudApi.updateTask(automationId, updates);
      // Schedule refresh to get updated data
      scheduleRefresh(500);
    } catch (err) {
      // Rollback via refresh on error
      fetchAutomations(0, { silent: true });
      throw err;
    }
  }, [scheduleRefresh, fetchAutomations]);

  const deleteAutomation = useCallback(async (automationId) => {
    // Optimistic: remove from list
    setAutomations(prev => prev.filter(a => a.id !== automationId));
    setTotalTasks(prev => Math.max(0, prev - 1));

    try {
      await cloudApi.deleteTask(automationId);
      // Schedule refresh to sync
      scheduleRefresh(500);
    } catch (err) {
      // Rollback via refresh
      fetchAutomations(0, { silent: true });
      throw err;
    }
  }, [scheduleRefresh, fetchAutomations]);

  const deleteAutomationsBulk = useCallback(async (automationIds) => {
    // Store original list for rollback
    const originalAutomations = automationsRef.current;

    // Optimistic: remove all from list
    setAutomations(prev => prev.filter(a => !automationIds.includes(a.id)));
    setTotalTasks(prev => Math.max(0, prev - automationIds.length));

    try {
      const result = await cloudApi.deleteTasksBulk(automationIds);
      // Schedule refresh to sync
      scheduleRefresh(500);
      return result;
    } catch (err) {
      // Rollback via setting original list
      setAutomations(originalAutomations);
      setTotalTasks(prev => prev + automationIds.length);
      throw err;
    }
  }, [scheduleRefresh]);

  const refresh = useCallback(() => {
    taskFingerprintsRef.current.clear();
    return fetchAutomations(0);
  }, [fetchAutomations]);

  return {
    // State
    automations,
    loading,
    filtering,
    error,
    summary,
    totalTasks,

    // Infinite scroll
    hasMore,
    loadingMore,
    loadMore,

    // Derived
    liveStats,
    scheduledAutomations,
    upcomingScheduled,

    // Actions
    runAutomation,
    stopAutomation,
    resumeAutomation,
    updateSchedule,
    updateAutomationSettings,
    deleteAutomation,
    deleteAutomationsBulk,
    refresh,

    // Action states
    runningIds,
    stoppingIds,
    resumingIds,
    loadingScheduleIds,
  };
}

export default useAutomationsSync;
